import React from "react"

// firebase
import { Auth, Firestore } from "services/firebase"
import {
  getDoc,
  setDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore"
import { onAuthStateChanged } from "firebase/auth"

const AuthContext = React.createContext()

function useAuth() {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error(`useAuth must be used within a AuthProvider`)
  }

  const { status } = context
  const [user, setUser] = context.user

  const isAuthentificated = React.useMemo(() => user !== null, [user])

  const claims = user?.reloadUserInfo?.customAttributes
    ? JSON.parse(user.reloadUserInfo.customAttributes)
    : null

  return {
    isAuthentificated,
    status,
    user,
    setUser,
    isAdmin: claims?.admin || false,
    restoKeys: claims?.resto_keys || [],
  }
}

function AuthProvider(props) {
  const [authuser, setAuthuser] = React.useState(null)
  const [authstatus, setAuthstatus] = React.useState("idle")

  const sub = React.useRef()

  React.useEffect(() => {
    if (!Auth) return

    sub.current = onAuthStateChanged(Auth, async (user) => {
      if (user) {
        const userData = await getDoc(doc(Firestore, "users", user.uid))

        if (!userData.exists()) {
          await setDoc(doc(Firestore, "users", user.uid), {
            email: user.email || null,
            created_at: serverTimestamp(),
          })
        }

        await updateDoc(doc(Firestore, "users", user.uid), {
          online_at: serverTimestamp(),
        })

        setAuthuser({ ...user, data: userData.data() })
      } else {
        setAuthuser(null)
      }

      setAuthstatus("ready")
    })

    return () => {
      if (sub.current) sub.current()
    }
  }, [])

  const user = React.useMemo(() => [authuser, setAuthuser], [authuser])
  const status = React.useMemo(() => authstatus, [authstatus])

  return <AuthContext.Provider value={{ user, status }} {...props} />
}

export { AuthProvider, useAuth }
